import React, {useState} from 'react';
import {Box, Checkbox, FormControlLabel, IconButton, Radio, TextField, Typography, Switch, Collapse, Button, Stack} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import MdEditor from "crqlar-react-common/dist/components/MdEditor";
import MdRenderer from "crqlar-react-common/dist/components/MdRenderer";
import {MultipleChoiceQuestion, Option} from "crqlar-react-common/dist/models/forms/form";
import {useTranslation} from "react-i18next";

interface MultipleChoiceInputProps {
  question: MultipleChoiceQuestion;
  onQuestionUpdate: (question: MultipleChoiceQuestion) => void;
  isSelected?: boolean;
  onSelect?: () => void;
}

const generateId = () => Math.random().toString(36).substring(2, 15);

const MultipleChoiceInput: React.FC<MultipleChoiceInputProps> = ({
  question,
  onQuestionUpdate,
  isSelected = false,
  onSelect
}) => {
  const {t} = useTranslation();
  const [expandedOptionId, setExpandedOptionId] = useState<string | null>(null);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onQuestionUpdate({
      ...question,
      title: e.target.value
    });
  };

  const handleDescriptionChange = (value?: string) => {
    onQuestionUpdate({
      ...question,
      description: value || ''
    });
  };

  const handleRequiredChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onQuestionUpdate({
      ...question,
      required: e.target.checked
    });
  };

  const handleOptionChange = (optionId: string, newLabel: string) => {
    const updatedOptions = (question.options || []).map(option =>
      option.id === optionId ? {...option, label: newLabel} : option
    );
    onQuestionUpdate({
      ...question,
      options: updatedOptions
    });
  };

  const handleOptionImageChange = (optionId: string, media: any) => {
    const updatedOptions = (question.options || []).map(option =>
      option.id === optionId ? {...option, imageMedia: media} : option
    );
    onQuestionUpdate({
      ...question,
      options: updatedOptions
    });
  };

  const handleAddOption = () => {
    const newOption: Option = {
      id: generateId(),
      label: t('Option {{number}}', {number: (question.options?.length || 0) + 1})
    };
    onQuestionUpdate({
      ...question,
      options: [...(question.options || []), newOption]
    });
  };

  const handleDeleteOption = (optionId: string) => {
    const updatedOptions = (question.options || []).filter(option => option.id !== optionId);
    onQuestionUpdate({
      ...question,
      options: updatedOptions
    });
  };

  const handleToggleMultiple = () => {
    onQuestionUpdate({
      ...question,
      allowMultiple: !question.allowMultiple
    });
  };

  const options = question.options || [];

  return (
    <Stack
      onClick={onSelect}
      sx={{
        p: 3,
        borderRadius: 1,
        border: isSelected ? '2px solid' : '1px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        backgroundColor: isSelected ? 'action.selected' : 'background.paper',
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: 'primary.light',
          backgroundColor: 'action.hover'
        },
        position: 'relative'
      }}
    >
      <Box sx={{alignSelf: 'flex-end'}}>
        <FormControlLabel
          control={
            <Switch
              checked={question.required || false}
              onChange={handleRequiredChange}
              onClick={(e) => e.stopPropagation()}
              size="small"
            />
          }
          label={<Typography variant="caption">{t('Required')}</Typography>}
          labelPlacement="start"
          onClick={(e) => e.stopPropagation()}
          sx={{m: 0}}
        />
      </Box>

      <TextField
        fullWidth
        label={t('Question Label')}
        value={question.title}
        onChange={handleTitleChange}
        variant="standard"
        sx={{mb: 2, pr: 12}}
        onClick={(e) => e.stopPropagation()}
      />

      {isSelected ? (
        <Box sx={{mb: 2}} onClick={(e) => e.stopPropagation()}>
          <MdEditor
            value={question.description || ''}
            onChange={handleDescriptionChange}
            placeholder={t('Question Description (optional)')}
            className={'mini-mdx'}
          />
        </Box>
      ) : (
        question.description && (
          <Box sx={{mb: 2}}>
            <MdRenderer>
              {question.description}
            </MdRenderer>
          </Box>
        )
      )}

      {isSelected && (
        <Box sx={{mb: 2}}>
          <FormControlLabel
            control={
              <Checkbox
                checked={question.allowMultiple || false}
                onChange={handleToggleMultiple}
                onClick={(e) => e.stopPropagation()}
              />
            }
            label={t('Allow multiple selections')}
          />
        </Box>
      )}

      <Box sx={{mb: 1}}>
        {options.map((option, index) => (
          <Box key={option.id} sx={{mb: 2}}>
            <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
              <FormControlLabel
                control={question.allowMultiple ? <Checkbox disabled /> : <Radio disabled />}
                label=""
                sx={{mr: 1}}
              />
              {isSelected ? (
                <TextField
                  fullWidth
                  value={option.label}
                  onChange={(e) => handleOptionChange(option.id, e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder={t('Option {{number}}', {number: index + 1})}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <Typography>{option.label}</Typography>
              )}
              {isSelected && (
                <>
                  {/*<IconButton*/}
                  {/*  size="small"*/}
                  {/*  onClick={(e) => {*/}
                  {/*    e.stopPropagation();*/}
                  {/*    setExpandedOptionId(expandedOptionId === option.id ? null : option.id);*/}
                  {/*  }}*/}
                  {/*  sx={{ml: 1}}*/}
                  {/*  color={option.imageMedia ? 'primary' : 'default'}*/}
                  {/*>*/}
                  {/*  <ImageIcon fontSize="small" />*/}
                  {/*</IconButton>*/}
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteOption(option.id);
                    }}
                    sx={{ml: 1}}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Box>

            {/* Image Picker Section */}
            {isSelected && (
              <Collapse in={expandedOptionId === option.id}>
                <Box
                  sx={{ml: 5, mt: 1}}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/*<MediaPickerField*/}
                  {/*  value={option.imageMedia}*/}
                  {/*  onChange={(media) => handleOptionImageChange(option.id, media)}*/}
                  {/*  mediaType="image"*/}
                  {/*  usageType={MediaUsageType.GENERAL}*/}
                  {/*  height={200}*/}
                  {/*  maxWidth="400px"*/}
                  {/*/>*/}
                </Box>
              </Collapse>
            )}

            {/* Show image preview when not editing */}
            {!isSelected && option.imageMedia?.mediaURI && (
              <Box sx={{ml: 5, mt: 1}}>
                <img
                  src={option.imageMedia.mediaURI}
                  alt={option.label}
                  style={{
                    maxWidth: '200px',
                    maxHeight: '150px',
                    borderRadius: '4px',
                    objectFit: 'cover'
                  }}
                />
              </Box>
            )}
          </Box>
        ))}
      </Box>

      {isSelected && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'primary.main',
            cursor: 'pointer',
            mt: 1,
            '&:hover': {opacity: 0.8}
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleAddOption();
          }}
        >
          <AddIcon fontSize="small" sx={{mr: 0.5}} />
          <Typography variant="body2">{t('Add option')}</Typography>
        </Box>
      )}
    </Stack>
  );
};

export default MultipleChoiceInput;
